import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SecurityContext,
  type SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  NxDialogService,
  NxModalConfig,
  NxModalRef,
  NxModalContentDirective,
  NxModalActionsDirective,
} from '@allianz/ng-aquila/modal';
import isEqual from 'lodash/isEqual';
import { NxIconComponent } from '@allianz/ng-aquila/icon';
import { NxCopytextComponent } from '@allianz/ng-aquila/copytext';
import { NgClass } from '@angular/common';
import { NxCheckboxComponent } from '@allianz/ng-aquila/checkbox';
import { NxButtonComponent } from '@allianz/ng-aquila/button';
import { TextPipe } from '@ps20-frontend/text-service';
import { ModalButtonType, ModalContent, ModalStyle } from './modal.interface';

export const ModalComponentSelector = 'ps20-frontend-modal';

@Component({
  selector: 'ps20-frontend-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  imports: [
    NxModalContentDirective,
    NxIconComponent,
    NxCopytextComponent,
    NgClass,
    NxCheckboxComponent,
    NxModalActionsDirective,
    NxButtonComponent,
    TextPipe,
  ],
})
export class ModalComponent implements OnChanges, AfterViewInit {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly appRef = inject(ApplicationRef);
  private readonly dialogService = inject(NxDialogService);
  private readonly renderer = inject(Renderer2);
  private readonly elementRef = inject(ElementRef);

  @ViewChild('ngIfModalBody') templateRef: TemplateRef<unknown>;
  @ViewChild('modalContainer') modalContainer: ElementRef;
  @ViewChild('modalIcon') modalIcon: ElementRef;
  @ViewChild('modalHeadline') modalHeadline: ElementRef;
  @ViewChild('modalSubheadline') modalSubheadline: ElementRef;
  @ViewChild('modalBody') modalBody: ElementRef;
  @ViewChild('modalCheckbox') modalCheckbox: ElementRef;
  @ViewChild('modalSecondaryButton') modalSecondaryButton: ElementRef;
  @ViewChild('modalPrimaryButton') modalPrimaryButton: ElementRef;

  @Input() modalOpen = false;
  @Input() textContent: ModalContent = null;
  @Input() styleObject: Partial<ModalStyle> = {};
  @Input() disableClose = false;
  @Output() buttonClicked = new EventEmitter();

  modalButtonTypes = ModalButtonType;
  userConfirmationCheckboxValue = false;
  templateDialogRef: NxModalRef<unknown>;

  constructor() {}

  buttonClick(buttonType: ModalButtonType): void {
    this.buttonClicked.emit(buttonType);
  }

  sanitizeText(text: string): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, text);
  }

  userConfirmationValueChanged(): void {
    this.userConfirmationCheckboxValue = !this.userConfirmationCheckboxValue;
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngAfterViewInit(): void {
    // Styles will be applied after modal opens
  }

  ngOnChanges(changes: SimpleChanges): void {
    const textPreviousValue = changes.textContent?.previousValue;
    const textCurrentValue = changes.textContent?.currentValue;
    const modalPreviousValue = changes.modalOpen?.previousValue;
    const modalCurrentValue = changes.modalOpen?.currentValue;
    if (
      this.modalOpen &&
      (!isEqual(textPreviousValue, textCurrentValue) || modalPreviousValue !== modalCurrentValue)
    ) {
      if (this.dialogService.openModals.length === 0) {
        // Defer to next microtask — avoids ExpressionChangedAfterItHasBeenCheckedError
        // when modal is triggered from ngOnChanges during change detection.
        setTimeout(() => {
          this.templateDialogRef = this.dialogService.open(this.templateRef, this.getModalConfig());
          this.templateDialogRef.afterClosed().subscribe(() => {
            this.buttonClicked.emit(this.modalButtonTypes.CLOSE);
          });
          setTimeout(() => this.applyModalStyles(), 0);
        }, 0);
      }
    }
    if (!this.modalOpen && this.templateDialogRef) {
      this.templateDialogRef.close();
    }
    this.appRef.tick();
  }

  private applyModalStyles(): void {
    if (!this.styleObject) {
      return;
    }

    // Find elements using data-cy attributes or class names
    const modalElement =
      this.elementRef.nativeElement.closest('nx-modal-container') ||
      document.getElementById('modal');

    if (!modalElement) {
      return;
    }

    this.applyStylesToElement(
      modalElement.querySelector('[id="modal"]'),
      this.styleObject.container,
    );
    this.applyStylesToElement(modalElement.querySelector('nx-icon'), this.styleObject.icon);
    this.applyStylesToElement(
      modalElement.querySelector('[data-cy="modal-headline"]'),
      this.styleObject.headline,
    );
    this.applyStylesToElement(
      modalElement.querySelector('[data-cy="modal-subheadline"]'),
      this.styleObject.subheadline,
    );
    this.applyStylesToElement(
      modalElement.querySelector('[data-cy="modal-content"]'),
      this.styleObject.body,
    );
    this.applyStylesToElement(
      modalElement.querySelector('[data-cy="modal-user-confirmation-checkbox"]'),
      this.styleObject.userConfirmationCheckbox,
    );
    this.applyStylesToElement(
      modalElement.querySelector('[data-cy="modal-secondary-button"]'),
      this.styleObject.secondaryButton,
    );
    this.applyStylesToElement(
      modalElement.querySelector('[data-cy="modal-primary-button"]'),
      this.styleObject.button,
    );
  }

  private applyStylesToElement(
    element: Element | null,
    styles: { [key: string]: string } | undefined,
  ): void {
    if (!element || !styles) {
      return;
    }

    Object.keys(styles).forEach(styleName => {
      this.renderer.setStyle(element, styleName, styles[styleName]);
    });
  }

  getModalConfig(): NxModalConfig {
    const dialogAriaLabel = 'label';
    const dialogSchliessenAriaLabel = 'label';

    return {
      disableClose: this.disableClose,
      autoFocus: true,
      ariaLabel: dialogAriaLabel,
      closeIconButtonLabel: dialogSchliessenAriaLabel,
      maxHeight: '768px',
      maxWidth: '1366px',
    };
  }
}
